<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { X } from '@lucide/vue';
import RichText from './RichText.vue';
// Окно с полным текстом согласия: «Принимаю» доступна после прокрутки до конца (если requireReading).
// anchorY — координата галочки в документе (для формы в iframe): окно ставится рядом с ней,
// а не по центру фрейма, который может быть высотой в несколько экранов.
const props = defineProps<{ title: string; text: string; requireReading: boolean; anchorY?: number }>();
const emit = defineEmits<{ accept: []; decline: []; close: []; placed: [top: number, height: number] }>();
const dialog = ref<HTMLElement>();
const backdrop = ref<HTMLElement>();
const anchoredTop = ref<number>();
const body = ref<HTMLElement>();
const readToEnd = ref(!props.requireReading);
const acceptButton = ref<HTMLButtonElement>();
function checkScroll() {
  const el = body.value;
  if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) readToEnd.value = true;
}
function onKey(event: KeyboardEvent) { if (event.key === 'Escape') emit('close'); }
onMounted(async () => {
  document.addEventListener('keydown', onKey);
  document.body.style.overflow = 'hidden';
  await nextTick();
  if (props.anchorY !== undefined && dialog.value) {
    const height = dialog.value.offsetHeight;
    // Координаты окна отсчитываются от подложки; над галочкой, если хватает места, иначе — от верха формы.
    const base = (backdrop.value?.getBoundingClientRect().top ?? 0) + window.scrollY;
    anchoredTop.value = Math.max(8, props.anchorY - base - height - 12);
    emit('placed', anchoredTop.value + base, height);
  }
  checkScroll();
  body.value?.focus({ preventScroll: true });
});
onUnmounted(() => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; });
</script>
<template>
  <div ref="backdrop" class="consent-dialog-backdrop" :class="{ anchored: anchorY !== undefined }" @click.self="emit('close')">
    <section ref="dialog" class="consent-dialog" role="dialog" aria-modal="true" aria-labelledby="consent-dialog-title" :style="anchoredTop !== undefined ? { top: `${anchoredTop}px` } : undefined">
      <header><RichText id="consent-dialog-title" tag="h2" :text="title"/><button type="button" class="icon-btn" aria-label="Закрыть" @click="emit('close')"><X :size="19"/></button></header>
      <div ref="body" class="consent-dialog-body" tabindex="0" @scroll="checkScroll"><RichText tag="div" :text="text"/></div>
      <footer>
        <button ref="acceptButton" type="button" class="btn btn-primary" :disabled="!readToEnd" @click="emit('accept')">Принимаю</button>
        <button type="button" class="btn btn-quiet" @click="emit('decline')">Не принимаю</button>
        <small v-if="!readToEnd">Прочитайте до конца</small>
      </footer>
    </section>
  </div>
</template>
